import {
    Button,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    useModal,
    usePagination,
    useSearch,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import { RootStackScreenProps } from "../../navigation";
import { formatDateTime, SearchDefinition } from "lib";
import { useTranslation } from "react-i18next";
import { useHandleMutationError, useSearchDefinition } from "../../hooks";
import React, { useCallback, useEffect } from "react";
import {
    GQVirtualPrintersQuery,
    useVirtualPrinterDeleteMutation,
    useVirtualPrintersQuery,
} from "graphql-sdk";
import { AdminContainer } from "../container";
import { View } from "react-native";
import { AvailableLocale } from "locales";
import { useAdminSession } from "../../session";

type ScreenProps = RootStackScreenProps<"VIRTUAL_PRINTERS">;

export function VirtualPrinters({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.printer");

    const theme = useTheme();
    const { render } = useModal();
    const [t] = useTranslation();
    const { createSearchDefinition } = useSearchDefinition();
    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
    });
    const [{ locale }] = useAdminSession(["locale"]);

    const styles = useThemedStyle(styleFunc);
    const defaultPrinterSearch: SearchDefinition = {
        name: createSearchDefinition("name"),
        createdAt: createSearchDefinition("createdAt"),
    };

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
    } = useSearch(defaultPrinterSearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const [deletePrinter] = useVirtualPrinterDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    // GraphQL
    const { data, loading, refetch } = useVirtualPrintersQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    const handleSelect = useCallback(
        (id: string) => {
            navigate("VIRTUAL_PRINTER_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            const deleteResponse = await render(onClose => (
                <ConfirmModal
                    headerText={t(
                        "backoffice.virtual_printer.delete",
                        "Delete virtual printer?"
                    )}
                    bodyText={t(
                        "backoffice.virtual_printer.delete_explain",
                        "Are you sure you want delete this virtual printer?"
                    )}
                    onClose={onClose}
                />
            ));

            if (!deleteResponse) {
                return;
            }

            await handleMutationError(
                async () => await deletePrinter({ variables: { id } }),
                t(
                    "backoffice.virtual_printer.deleted",
                    "Virtual printer deleted"
                ),
                async () => {
                    await refetch();
                }
            );
        },
        [deletePrinter, handleMutationError, refetch, render, t]
    );

    const renderPrinterRow = useCallback(
        (
            item: GQVirtualPrintersQuery["virtualPrinters"]["data"][0],
            index: number
        ) => (
            <DataTable.Row
                key={`virtualPrinter_${index}`}
                onPress={() => handleSelect(item.id)}
                style={index % 2 ? theme.styles.oddRow : null}
            >
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell>
                    {item.printer?.name ??
                        t(
                            "backoffice.virtual_printer.no_printer",
                            "No printer"
                        )}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    <IconButton
                        color={theme.colors.secondary}
                        name="edit"
                        onPress={() => handleSelect(item.id)}
                    />
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            handleDelete,
            handleSelect,
            locale,
            styles.iconColumn,
            t,
            theme.colors.secondary,
            theme.styles.oddRow,
        ]
    );

    return (
        <AdminContainer>
            <View style={[theme.styles.row, styles.container]}>
                <Button
                    onPress={() => {
                        navigate("VIRTUAL_PRINTER_CREATE");
                    }}
                    style={styles.button}
                >
                    {t("backoffice.virtual_printer.create", "Create")}
                </Button>
                <Spacer space={1} />
                {searchIndicator}
            </View>
            <Spacer space={2} />
            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t(
                                "backoffice.virtual_printer.name.header",
                                "Name"
                            )}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.virtual_printer.name.printer",
                                "Printer"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t(
                                "backoffice.virtual_printer.created_at",
                                "Created at"
                            )}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {!loading ? (
                        data?.virtualPrinters.data.map(renderPrinterRow)
                    ) : (
                        <Loading />
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.virtualPrinters.pagination.pages}
                        itemCount={data?.virtualPrinters.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    container: {
        justifyContent: "flex-end",
        flexWrap: "wrap",
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 40 },
    smallColumn: {
        flexBasis: 140,
        flexGrow: 0,
    },
});
